import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import FilterBar, { emptyFilters, type FilterState, type SortKey } from "./components/FilterBar";

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState>(() => ({
    ...emptyFilters,
    types: searchParams.get("type") ? [searchParams.get("type") as string] : [],
    genres: searchParams.get("genre") ? [searchParams.get("genre") as string] : [],
    regions: searchParams.get("region") ? [searchParams.get("region") as string] : [],
  }));
  const initialSort = (searchParams.get("sort") as SortKey) || "latest";
  const [sort, setSort] = useState<SortKey>(["latest", "rating", "hot"].includes(initialSort) ? initialSort : "latest");
  const [page, setPage] = useState(1);

  const handleToggle = (key: keyof FilterState, value: string | number) => {
    setPage(1);
    setFilters((prev) => {
      const list = prev[key] as (string | number)[];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [key]: next } as FilterState;
    });
  };

  const handleSetRating = (value: number) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, minRating: value }));
  };

  const handleSortChange = (next: SortKey) => {
    setPage(1);
    setSort(next);
  };

  const handleReset = () => {
    setPage(1);
    setFilters(emptyFilters);
  };

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (filters.types.length && !filters.types.includes(m.type)) return false;
      if (filters.genres.length && !m.genres.some((g) => filters.genres.includes(g))) return false;
      if (filters.regions.length && !filters.regions.includes(m.region)) return false;
      if (filters.years.length && !filters.years.includes(m.year)) return false;
      if (filters.minRating && m.rating < filters.minRating) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "hot") sorted.sort((a, b) => b.views - a.views);
    else sorted.sort((a, b) => b.year - a.year);
    return sorted;
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const currentType = filters.types.length === 1 ? filters.types[0] : "";
  const pageTitle = currentType ? `${currentType}分类` : "影视分类";
  const seoTitle = `${pageTitle} - 一本道影视大全`;
  const seoDesc = `${pageTitle}页面支持按题材、地区、年份、评分多维筛选，快速定位你感兴趣的${currentType || "电影与电视剧"}作品。`;

  useSeo({ title: seoTitle, description: seoDesc, keywords: "影视分类,电影,电视剧,影视筛选,一本道影视大全" });

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-3">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">影视分类</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">{pageTitle}</h1>
          <p className="mt-3 text-sm text-foreground-500 max-w-2xl">
            汇集最新电影与电视剧资源，支持按作品类型、题材、地区、年份与评分多维筛选，帮助你快速找到心仪之作。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <FilterBar
            filters={filters}
            sort={sort}
            resultCount={filtered.length}
            onToggle={handleToggle}
            onSetRating={handleSetRating}
            onSortChange={handleSortChange}
            onReset={handleReset}
          />

          <div className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center justify-between gap-4 mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品
              </p>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {[
                  { key: "latest" as SortKey, label: "最新" },
                  { key: "rating" as SortKey, label: "评分" },
                  { key: "hot" as SortKey, label: "热门" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => handleSortChange(opt.key)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sort === opt.key ? "bg-accent-500 text-primary-950" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {pageItems.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      type="button"
                      disabled={safePage === 1}
                      onClick={() => setPage(safePage - 1)}
                      className="w-10 h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-xl"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        className={`w-10 h-10 rounded-md text-sm font-medium cursor-pointer ${
                          p === safePage
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-300 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={safePage === totalPages}
                      onClick={() => setPage(safePage + 1)}
                      className="w-10 h-10 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-xl"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 rounded-lg border border-dashed border-background-300 bg-background-50">
                <span className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">没有找到匹配的影视作品</h3>
                <p className="mt-2 text-sm text-foreground-500">试试减少筛选条件，或重置后重新浏览</p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-6 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}